import { Button, DropZone, Icon, TextField } from "@shopify/polaris"
import { CancelSmallMinor, CirclePlusMajor } from "@shopify/polaris-icons"
import React, { useCallback, useEffect, useState } from "react"
import { parseCsvAndFindProductUrls } from "../../utils/csvParse.js"
import { useTranslation } from "react-i18next"

export const Multilink = ({ loading, onSubmit }) => {
	const [multilinkProductUrls, setMultilinkProductUrls] = useState([""])
	const [hoveringOverRow, setHoveringOverRow] = useState(null)
	const [showAllRows, setShowAllRows] = useState(true)
	const { t } = useTranslation()

	const handleDropZoneDrop = useCallback(
		(dropFiles, acceptedFiles, rejectedFiles) => {
			if (acceptedFiles.length) {
				const csvArr = parseCsvAndFindProductUrls(
					acceptedFiles[0],
					setMultilinkProductUrls,
					submit,
				)
			}
		},
		[],
	)

	const changeProductsUrl = (value, index) => {
		const newMultilinkProductUrls = [...multilinkProductUrls]
		newMultilinkProductUrls[index] = value
		setMultilinkProductUrls(newMultilinkProductUrls)
	}

	const addRow = () => {
		const newMultilinkProductUrls = [...multilinkProductUrls]
		newMultilinkProductUrls.push("")
		setMultilinkProductUrls(newMultilinkProductUrls)
		setShowAllRows(true)
	}

	const removeRow = (index) => {
		const newMultilinkProductUrls = [...multilinkProductUrls]
		newMultilinkProductUrls.splice(index - 1, 1)
		setMultilinkProductUrls(newMultilinkProductUrls)
	}

	const submit = (productUrls) => {
		if (productUrls?.length && !loading) {
			onSubmit(productUrls)
			// setShowAllRows(false)
		}
	}

	const downloadCSVTemplate = () => {
		const element = document.createElement("a")
		element.setAttribute(
			"href",
			`data:text/csv;charset=utf-8,${encodeURIComponent("Product URL\nhttps://store.com/product")}`,
		)
		element.setAttribute("download", "kopy-multilink-template.csv")

		element.style.display = "none"
		document.body.appendChild(element)

		element.click()

		document.body.removeChild(element)
	}

	const numberBadge = (number) => {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<div
					onMouseOver={() => setHoveringOverRow(number)}
					onMouseLeave={() => setHoveringOverRow(null)}
					style={{
						width: "1.875rem",
						height: "1.875rem",
						borderRadius: "100%",
						background: "#fff",
						textAlign: "center",
						lineHeight: "1.6875rem",
						fontSize: "0.8rem",
						border: "1px solid #bbb",
						cursor:
							hoveringOverRow === number && number > 1
								? "pointer"
								: "",
					}}
				>
					{hoveringOverRow === number && number > 1 ? (
						<div
							style={{ marginTop: "0.25rem" }}
							onClick={() => removeRow(number)}
						>
							<Icon source={CancelSmallMinor} color="subdued" />
						</div>
					) : (
						number
					)}
				</div>
			</div>
		)
	}

	const shortcutSymbols = () => {
		if (navigator.userAgent.indexOf("Win") != -1) {
			return "(Ctrl + ⏎)"
		} else if (navigator.userAgent.indexOf("Mac") != -1) {
			return "(⌘ + ⏎)"
		} else if (navigator.userAgent.indexOf("Linux") != -1) {
			return "(Ctrl + ⏎)"
		}
	}

	useEffect(() => {
		const onKeyDown = (e) => {
			if ((e.ctrlKey || e.metaKey) & (e.keyCode === 13)) {
				return submit(multilinkProductUrls)
			}

			if (
				e.target.className.includes("Polaris-TextField__Input") &&
				e.key === "Enter"
			) {
				return addRow()
			}
		}
		window.addEventListener("keydown", onKeyDown)

		return () => {
			window.removeEventListener("keydown", onKeyDown)
		}
	}, [multilinkProductUrls])

	return (
		<div>
			<TextField
				placeholder={t("multiple.single_product_url")}
				inputMode="url"
				clearButton
				onClearButtonClick={() => changeProductsUrl("", 0)}
				value={multilinkProductUrls?.[0] || ""}
				onChange={(change) => changeProductsUrl(change, 0)}
				connectedLeft={numberBadge(1)}
				connectedRight={
					<Button
						onClick={() => submit(multilinkProductUrls)}
						loading={loading}
						disabled={loading}
					>
						{t("multiple.import_products")} {shortcutSymbols()}
					</Button>
				}
			/>

			{multilinkProductUrls
				.slice(1, showAllRows ? undefined : 3)
				.map((_, index) => (
					<div style={{ marginTop: "0.5rem" }} key={index}>
						<TextField
							inputMode="url"
							clearButton
							autoFocus
							onClearButtonClick={() =>
								changeProductsUrl("", index + 1)
							}
							value={multilinkProductUrls[index + 1]}
							onChange={(change) =>
								changeProductsUrl(change, index + 1)
							}
							connectedLeft={numberBadge(index + 2)}
						/>
					</div>
				))}

			{!loading && showAllRows && (
				<div
					style={{
						display: "flex",
						gap: "0.5rem",
						width: "100%",
						marginTop: "0.5rem",
						cursor: "pointer",
					}}
					onClick={addRow}
				>
					<div
						style={{
							width: "1.625rem",
							fill: "#bbb",
							paddingTop: "0.375rem",
							paddingLeft: "0.25rem",
						}}
					>
						<Icon source={CirclePlusMajor} />
					</div>
					<div
						style={{
							flex: "1",
							height: "2.125rem",
							background: "#fff",
							border: "1px solid #ccc",
							boxShadow:
								"0 1px 1px rgba(0,0,0,0.1), 0 2px 2px rgba(0,0,0,0.1)",
							borderRadius: "1rem",
						}}
					></div>
				</div>
			)}

			{!showAllRows && multilinkProductUrls.length > 3 && (
				<div style={{ marginTop: "0.5rem", textAlign: "center" }}>
					<Button plain onClick={() => setShowAllRows(true)}>
						{t("multiple.show")} {multilinkProductUrls.length - 3}{" "}
						more
					</Button>
				</div>
			)}

			{multilinkProductUrls.length === 1 && !loading && (
				<div style={{ marginTop: "1rem" }}>
					<div
						style={{
							width: "100%",
							textAlign: "center",
							marginTop: "2rem",
							marginBottom: "2rem",
						}}
					>
						{t("multiple.or")}
					</div>
					<DropZone
						accept="text/csv"
						allowMultiple={false}
						onDrop={handleDropZoneDrop}
					>
						<DropZone.FileUpload
							actionTitle={t(
								"multiple.import_a_csv_file_containing_product_links",
							)}
						/>
					</DropZone>
					<div
						style={{
							marginTop: "1rem",
							width: "100%",
							display: "flex",
							justifyContent: "center",
						}}
					>
						<Button plain onClick={downloadCSVTemplate}>
							{t("multiple.download_csv_template")}
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
